import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { sanitizeRumEvent, setConsoleErrorScrubbing, toDatadogUser } from './datadog-redaction';

import { platformBrowser } from '@angular/platform-browser';
import { AppModule } from './app/app.module';
import { DATADOG_CLIENT_TOKEN } from './datadog_config';
import { environment } from './environments/environment';

export function getBaseUrl() { return document.getElementsByTagName('base')[0].href; }

const providers = [ { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] } ];

if (environment.production) { enableProdMode(); }

// Get the JSON web token from local storage
const jwt = localStorage.getItem('jwt') && !localStorage.getItem('jwt').startsWith('"') 
	? localStorage.getItem('jwt')
	: JSON.parse(localStorage.getItem('jwt'));

// If we have a JSON web token, fetch user info before bootstrapping Angular
if (jwt) {
	fetch(getBaseUrl() + 'api/Account/GetUserInfo', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${jwt}`,
		},
	})
		// Parse JSON if the response is OK, otherwise reject with the status code
		.then(res => res.ok ? res.json() : Promise.reject(new Error(res.statusText)))
		// Store the user info and token in localStorage for use during bootstrap
		.then(model => {
			if (model.user === null) { localStorage.removeItem('jwt'); }
			localStorage.setItem('user', JSON.stringify(model.user));
			localStorage.setItem('jwt', JSON.stringify(jwt));
			// PCG Admin Settings: only an explicit "off" lets console errors reach Datadog unscrubbed.
			setConsoleErrorScrubbing(model.telemetry?.scrubConsoleErrors !== false);
		})
		// On any failure (network, 401, 403, etc.), clear stale auth state
		.catch(() => {
			localStorage.removeItem('jwt');
			localStorage.removeItem('user');
		})
		// Always bootstrap Angular regardless of success or failure
		.finally(() => {
			bootstrapAngular();
			bootstrapDataDogRum();
		});
} else {
	// No token found — clear any stale user data and bootstrap immediately
	localStorage.removeItem('user');
	bootstrapAngular();
}

function bootstrapAngular() { platformBrowser(providers).bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()], }).catch(err => console.log(err)); }

function bootstrapDataDogRum() {
    if (environment.production) {
        datadogRum.init({
            applicationId: 'fe1f5ad7-8562-45f4-8537-8622e5931e69'
			, clientToken: DATADOG_CLIENT_TOKEN
			, site: 'us5.datadoghq.com'
			, service: 'homer'
			, allowedTracingUrls: ["https://www.fdepcs.com"]
			, env: 'production'
            // Specify a version number to identify the deployed version of your application in Datadog
            // This will be set within a task in your CI pipeline.
            , version: '362172dedd'
			, sessionSampleRate: 100
			, sessionReplaySampleRate: 100
			, trackUserInteractions: true
			, trackResources: true
			, trackLongTasks: true
			// Session Replay keeps page text readable (module names, button labels, product names,
			// error and success messages) and masks form input. PHI and encrypted fields shown as page
			// text are masked where they are rendered, with data-dd-privacy="mask" or the
			// dd-privacy-mask class - see docs/security/logging-data-classification-standard.md.
			, defaultPrivacyLevel: 'mask-user-input'
			// Click action names honour those masks: a click on a masked value is recorded as
			// "Masked Element", not as the value's text.
			, enablePrivacyForActionName: true
			// Query strings (SSO tickets, redirect targets) and credential-shaped text are stripped
			// from every event before it leaves the browser.
			, beforeSend: sanitizeRumEvent
        });
		setDatadogUser();
	}
}

// Datadog receives the user's id and name, so sessions can be searched by who was using them.
function setDatadogUser() {
	let user = toDatadogUser(JSON.parse(localStorage.getItem('user')));
	if (user != null) { datadogRum.setUser(user); }
}
